import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faArrowLeft, faSpinner, faLock } from '@fortawesome/free-solid-svg-icons';
import { AuroraBackground } from '../ui/aurora-background';
import { businessAuthAPI } from '../../services/api';
import SEO from '../SEO';
import { seoConfig } from '../../utils/seoData';

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const { lang } = useParams<{ lang: string }>();
  const currentLang = lang || 'tr';
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await businessAuthAPI.forgotPassword({ email });
      setSuccess(true);
      // Navigate to code verification page after a short delay
      setTimeout(() => {
        navigate(`/${currentLang}/reset-code-verification`, { state: { email } });
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Kod gönderilirken bir hata oluştu.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SEO {...seoConfig.login} />
      <AuroraBackground className="w-full h-full overflow-scroll">
        <div className="w-full min-h-screen flex items-center justify-center relative overflow-hidden py-10">
          {/* Background Effects */}
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-purple-900/20 to-gray-900"></div>
          <div className="absolute inset-0 opacity-20" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>

          {/* Floating Elements */}
          <div className="absolute top-20 left-10 w-20 h-20 bg-purple-500/10 rounded-full blur-xl animate-float"></div>
          <div className="absolute top-40 right-20 w-32 h-32 bg-blue-500/10 rounded-full blur-xl animate-float" style={{ animationDelay: '1s' }}></div>
          <div className="absolute bottom-20 left-1/4 w-24 h-24 bg-pink-500/10 rounded-full blur-xl animate-float" style={{ animationDelay: '2s' }}></div>

          <div className="relative z-10 w-full max-w-md mx-auto px-6">
            <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-3xl p-8 md:p-12 shadow-2xl">
              {/* Back Button */}
              <button
                onClick={() => navigate(`/${currentLang}/login`)}
                className="flex items-center space-x-2 text-white/80 hover:text-white transition-colors mb-6"
              >
                <FontAwesomeIcon icon={faArrowLeft} />
                <span>Geri</span>
              </button>

              {/* Header */}
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-purple-500/20 to-purple-700/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <FontAwesomeIcon icon={faLock} className="text-purple-400 text-3xl" />
                </div>
                <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Şifremi Unuttum
                </h1>
                <p className="text-white/70">
                  E-posta adresinizi girin, size şifre sıfırlama kodu gönderelim.
                </p>
              </div>

              {success && (
                <div className="bg-green-500/20 text-green-400 p-3 rounded-lg text-center mb-4">
                  Kod başarıyla gönderildi! Yönlendiriliyorsunuz...
                </div>
              )}

              {error && (
                <div className="bg-red-500/20 text-red-400 p-3 rounded-lg text-center mb-4">
                  {error}
                </div>
              )}

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Email Field */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-white/80 mb-2">
                    E-posta Adresi
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FontAwesomeIcon icon={faEnvelope} className="text-white/40 text-sm" />
                    </div>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
                      placeholder="ornek@email.com"
                      required
                      disabled={isLoading || success}
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isLoading || success}
                  className="w-full bg-gradient-to-r from-purple-500 to-purple-700 px-8 py-4 rounded-xl text-white font-semibold hover:from-purple-600 hover:to-purple-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center space-x-2">
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                      <span>Gönderiliyor...</span>
                    </div>
                  ) : (
                    'Şifre Sıfırlama Kodu Gönder'
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </AuroraBackground>
    </>
  );
};

export default ForgotPassword;

